
<template>
  <a-modal
    v-model="proxyVisible"
    class="hkl-modal"
    v-bind="bindAttrs"
    v-on="onListeners"
  >
    <template slot="title">
      <slot name="title" />
    </template>
    <slot />
    <div class="hkl-modal__footer">
      <slot name="footer">
        <HKLButton
          v-if="hasCancelBtn"
          color="darkblue"
          :action="cancelBtnAction"
          @click="onClose"
        />
        <HKLButton
          v-if="hasOkBtn"
          :disabled="okBtnDisabled"
          color="orange"
          :action="okBtnAction"
          @click="onClick"
        />
      </slot>
    </div>
  </a-modal>
</template>
<script>
import { Modal } from 'ant-design-vue'
import HKLButton from '@/controls/Button/HKLButton'

export const ModalSize = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
}

const SizeMap = {
  [ModalSize.SMALL]: 500,
  [ModalSize.MEDIUM]: 700,
  [ModalSize.LARGE]: '75%',
}

const DialogStyleMap = {
  default: {
    'top': '150px',
  },
  stickyTop: {
    'top': '0px',
  },
}

const Event = {
  OK_CLICK: 'onOkClick',
  CLOSE_CLICK: 'onCloseClick',
}

/**
 * Modal for HKLand
 * @type {import('vue').Component}
 * @props {string} [size='small']
 * The param can use
 * 'small' 'medium' 'large'
 * @props {string} [okBtnAction='ok'] Ok button wording. Refer action.json
 * @props {string} [cancelBtnAction='cancel'] Cancel button wording. Refer action.json
 * @props {function} [afterClose] Modal 完全关闭后的回调
 * @props {boolean} [closable] Modal 是否显示右上角的关闭按钮
 * @props {boolean} [keyboard] 是否支持键盘 esc 关闭
 * @props {boolean} [hasOkBtn] should show OK Button
 * @props {boolean} [hasCancelBtn] should show Cancel Button
 * @props {Number} [zIndex] 设置 Modal 的 z-index
 * @props {boolean} [getContainer = () => document.body] Modal 指定 Modal 挂载的 HTML 节点
 * TODO 如果想将pop-up的顶部，自动滚动到屏幕顶部，可以使用下面方法
 *  setTimeout(() => {
      $ref.modal.$el.querySelector('.ant-modal').scrollIntoView({block: 'center'})
    }, 1000)
 * Support the props of follow link except @ok event
 * https://1x.antdv.com/components/modal-cn/
 * @example
 * ```jsx
 * <HKLModal size="small" okBtnAction="submit" @onOkClick="() => {}" />
 * ```
 * @example
 * ```jsx
 * <HKLModal width="300" okBtnAction="submit" @onOkClick="() => {}" />
 * ```
 * @example
 * ```jsx
 * <HKLModal width="50%" okBtnAction="submit" @onOkClick="() => {}" />
 * ```
 */
const HKLModal = {
  data () {
    return {
      proxyVisible: false,
    }
  },
  components: {
    Modal,
    HKLButton,
  },
  props: {
    // small/medium/large
    size: {
      type: [String, Number],
      default: ModalSize.SMALL,
      validator: (val) => {
        return Object.keys(SizeMap).includes(val) || typeof val === 'number'
      },
    },
    okBtnAction: {
      type: String,
      default: 'ok',
    },
    okBtnDisabled: {
      type: [Boolean, Function],
      default: false,
    },
    cancelBtnAction: {
      type: String,
      default: 'cancel',
    },
    hasOkBtn: {
      type: Boolean,
      default: true,
    },
    hasCancelBtn: {
      type: Boolean,
      default: true,
    },
    beforeOkClick: {
      type: Function,
      default: () => true,
    },
    // default|stickyTop
    dialogStyleOpt: {
      type: String,
      default: 'default',
      validator: (val) => {
        return Object.keys(DialogStyleMap).includes(val)
      },
    },
  },
  computed: {
    modalWidth () {
      return SizeMap[this.size] || this.size
    },
    bindAttrs () {
      const defaultAttrs = {
        width: this.modalWidth,
        footer: null,
        maskClosable: false,
        closable: false,
        destroyOnClose: true,
        dialogStyle: DialogStyleMap[this.dialogStyleOpt],
      }
      return Object.assign({}, defaultAttrs, this.$attrs)
    },
    onListeners () {
      const {
        [Event.OK_CLICK]: __,
        [Event.CLOSE_CLICK]: ___,
        ...listeners
      } = this.$listeners
      return listeners
    },
  },
  methods: {
    async onClick () {
      const isValid = await this.beforeOkClick()
      if (!isValid) {
        return
      }
      await this.$emit(Event.OK_CLICK)
      this.close()
    },
    open () {
      this.proxyVisible = true
      this.$emit('open')
    },
    close () {
      this.proxyVisible = false
    },
    async onClose () {
      await this.$emit(Event.CLOSE_CLICK)
      this.close()
    },
  },
}

export default HKLModal
</script>
<style lang="scss" scoped>
.hkl-modal {
  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    .btn {
      margin-right: 5px;
    }
    .btn:last-child {
      margin-right: 0;
    }
  }
  &.align-btn-center {
    .hkl-modal__footer {
      justify-content: center;
    }
  }
  ::v-deep {
    .ant-modal {
      color: #0C3A58;
      max-width: 75rem;
    }
    .ant-modal-content {
      padding: 30px;
      background-color: #fefefe;
      border: 1px solid #cacaca;
      border-radius: 0;
    }
    .ant-modal-header {
      padding: 0;
      background-color: #fefefe;
      border-bottom: none;
      border-radius: 0;
    }
    .ant-modal-title {
      color: #0C3A58;
      font-weight: bold;
      font-size: 14px;
    }
    .ant-modal-body {
      padding: 0;
      margin-top: 20px;
    }
    .ant-modal-close {
      top: 5px;
      right: 5px;
    }
    .ant-modal-close-x {
      width: 30px;
      height: 30px;
      font-size: 14px;
      line-height: 30px;
      color: #0c3a58;
    }
    .ant-modal-close-icon svg path {
      stroke: #0c3a58;
      stroke-width: 70;
      stroke-linecap: butt;
    }
  }
}
</style>
